'use es6';

import { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { debounce } from 'underscore';
import Raven from 'raven-js';
import unescapedText from 'I18n/utils/unescapedText';
import { Select } from 'foundations-components/transitional/components/Select';
import { Flex } from 'foundations-components/components/Flex';
import { Tag } from 'foundations-components/transitional/components/Tag';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Text } from 'foundations-components/components/Text';
import * as PagesAction from 'simple-menus-core/actions/Pages';
import { getPage, getPagesFetchRequestStatus } from 'simple-menus-core/selectors/pages';
import StatesStatusGroups from 'ContentUtils/constants/StateStatusGroups';
import { DEFAULT_DEBOUNCE_TIME, PAGES_FETCH_LIMIT } from '../../constants/menus';
import { RequestStatuses } from '../../constants/requestStatuses';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function SelectedOption({
  isPublished,
  readOnly,
  selectedPageName,
  selectedPageEditorUrl
}) {
  const isDraftPage = !isPublished && selectedPageEditorUrl;
  return /*#__PURE__*/_jsxs(Flex, {
    children: [isDraftPage && /*#__PURE__*/_jsx(Tooltip, {
      disabled: readOnly,
      title: unescapedText('simpleMenusLib.components.pagesLinkSelect.draft.tooltip'),
      children: /*#__PURE__*/_jsx(Tag, {
        flexShrink: 0,
        marginInlineEnd: "200",
        use: "warning",
        children: unescapedText('simpleMenusLib.components.pagesLinkSelect.draft.label')
      })
    }), selectedPageName]
  });
}
function PagesLinkSelect({
  isPublished,
  isVisible,
  readOnly,
  selectedPageId,
  selectedPageName,
  selectedPageEditorUrl,
  onChange = () => {}
}) {
  const dispatch = useDispatch();
  const page = useSelector(state => getPage(state, {
    pageLinkId: selectedPageId
  }));
  const pagesFetchRequestStatus = useSelector(getPagesFetchRequestStatus);
  const [isOpen, setIsOpen] = useState(false);
  const [pagesRequestFulfilled, setPagesRequestFulfilled] = useState(!!page);
  const pageRequestRetried = useRef(false);
  const pageFetchFailed = pagesRequestFulfilled && !!selectedPageId && !page && pageRequestRetried.current && pagesFetchRequestStatus === RequestStatuses.FAILED;
  useEffect(() => {
    if (!pagesRequestFulfilled && isVisible && [RequestStatuses.SUCCEEDED, RequestStatuses.FAILED].includes(pagesFetchRequestStatus)) {
      if (!page && !pageRequestRetried.current) {
        dispatch(PagesAction.fetchPagesById(selectedPageId));
        pageRequestRetried.current = true;
        setPagesRequestFulfilled(false);
      } else {
        setPagesRequestFulfilled(true);
      }
    }
  }, [isVisible, page, selectedPageId, pagesRequestFulfilled, pagesFetchRequestStatus, dispatch]);
  const handleSelectOpenChange = ({
    target: {
      value: selectIsOpen
    }
  }) => {
    /* Allow dropdown to trigger onChange before switching */
    setTimeout(() => {
      setIsOpen(selectIsOpen);
      if (!selectIsOpen) {
        dispatch(PagesAction.clearCache());
      }
    }, 0);
  };
  const handleReadonlySelectOpenChange = () => {
    setIsOpen(true);
  };
  const handlePageLinkChange = evt => {
    if (onChange) {
      onChange(evt);
    }
    const id = evt.target.value;
    dispatch(PagesAction.saveCachedPage(id));
  };
  const debouncedLoadOptions = useMemo(() => debounce((searchQuery, callback, {
    pageOffset = 0
  } = {}) => {
    if (!isOpen) return;
    const query = {
      offset: pageOffset,
      limit: PAGES_FETCH_LIMIT,
      name: searchQuery
    };
    dispatch(PagesAction.fetchPages(query)).then(response => {
      const {
        results,
        hasMore,
        total,
        offset
      } = response;
      const pagesOptions = results.map(pageObject => {
        const isDraft = !StatesStatusGroups.PUBLISHED.includes(pageObject.state);
        return {
          text: /*#__PURE__*/_jsx(Text, {
            textAlign: "start",
            children: pageObject.name
          }),
          value: pageObject.id,
          help: pageObject.absoluteUrl,
          badge: isDraft ? /*#__PURE__*/_jsx(Tag, {
            flexShrink: 0,
            use: "warning",
            children: unescapedText('simpleMenusLib.components.pagesLinkSelect.draft.label')
          }) : null
        };
      });
      callback(null, {
        options: pagesOptions,
        pagination: {
          hasMore,
          length: total,
          pageOffset: offset
        }
      });
    }).catch(error => {
      callback(error);
      Raven.captureException(new Error('simple-menus-lib: failed to fetch pages', {
        cause: error
      }));
    });
  }, DEFAULT_DEBOUNCE_TIME), [isOpen, dispatch]);
  // The selected option display when the dropdown is closed
  if (!isOpen || readOnly) {
    return /*#__PURE__*/_jsx(Select, {
      menuWidth: 350,
      error: pageFetchFailed,
      disabled: readOnly,
      placeholder: /*#__PURE__*/_jsx(SelectedOption, {
        isPublished: isPublished,
        readOnly: readOnly,
        selectedPageName: selectedPageName,
        selectedPageEditorUrl: selectedPageEditorUrl
      }),
      value: null,
      onOpenChange: handleReadonlySelectOpenChange
    });
  }
  return /*#__PURE__*/_jsx(Select, {
    menuWidth: 350,
    cache: null,
    defaultOpen: true,
    filterOptions: false,
    error: pageFetchFailed,
    disabled: readOnly,
    placeholder: /*#__PURE__*/_jsx(SelectedOption, {
      isPublished: isPublished,
      readOnly: readOnly,
      selectedPageName: selectedPageName,
      selectedPageEditorUrl: selectedPageEditorUrl
    }),
    loadOptions: debouncedLoadOptions,
    onChange: handlePageLinkChange,
    onOpenChange: handleSelectOpenChange
  });
}
PagesLinkSelect.propTypes = {
  isPublished: PropTypes.bool,
  isVisible: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool,
  selectedPageId: PropTypes.number,
  selectedPageName: PropTypes.string,
  pageEditorUrl: PropTypes.string,
  onChange: PropTypes.func.isRequired
};
export default PagesLinkSelect;